import { useAutoneTranslation } from '@autone/translations';
import {
  AutoneTooltip,
  cn,
  createColumnLoadingGuards,
  DataTableCellContent,
  DataTableCellTitle,
  DataTableCellUnits,
  type DataTableLoadingObject,
} from '@autone/ui';
import { type ColumnHelper } from '@tanstack/react-table';
import { Info } from 'lucide-react';

import { type APP_NAME } from '../../../../consts';
import { type AssortmentProductItem } from '../../../../redux/assortment';

const COLUMN_ID = 'initial_allocation' as const;

export type InitialAllocationColumnId = typeof COLUMN_ID;

interface InitialAllocationFields {
  initial_allocation: AssortmentProductItem['initial_allocation'];
  is_assorted: AssortmentProductItem['is_assorted'];
}

const { columnCellGuard, accessorFnGuard } =
  createColumnLoadingGuards<InitialAllocationFields>();

export const createInitialAllocationColumn = <
  T extends InitialAllocationFields | DataTableLoadingObject,
>(
  columnHelper: ColumnHelper<T>,
) =>
  columnHelper.accessor(
    accessorFnGuard((row) => row.initial_allocation),
    {
      id: COLUMN_ID,
      size: 200,
      header: function InitialAllocationHeader() {
        const { t } = useAutoneTranslation<typeof APP_NAME>();

        return (
          <DataTableCellContent align={'right'}>
            <DataTableCellTitle className="flex flex-row gap-2 items-center uppercase font-medium whitespace-normal text-right">
              {t('table-columns.initial-allocation')}
              <AutoneTooltip
                text={t(
                  'table-column-tooltips.initial-allocation.product-view-table',
                )}
              >
                <Info className="inline" size={14} />
              </AutoneTooltip>
            </DataTableCellTitle>
          </DataTableCellContent>
        );
      },
      cell: (ctx) =>
        columnCellGuard({
          ctx,
          renderCell: function InitialAllocationCell({ row }) {
            const { initial_allocation, is_assorted } = row.original;

            return (
              <DataTableCellContent align={'right'}>
                <DataTableCellTitle
                  className={cn(!is_assorted && 'text-grey-40')}
                >
                  <DataTableCellUnits value={initial_allocation ?? undefined} />
                </DataTableCellTitle>
              </DataTableCellContent>
            );
          },
        }),
    },
  );
